<script setup>
import { ref, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";

var loading = ref(false);

var showWarningDialog = ref(false);
var popupPane = ref(false);
var popupTitle = ref("");
var popupMessage = ref("");

// Reactive variables for parsed CSV data
var csvHeaders = ref([]);
var csvRows = ref([]);
var selectedDate = ref(null);

var referenceNumber = ref("");

const today = new Date().toISOString().split("T")[0]; // Get today's date in yyyy-mm-dd format, which the date picker uses.

async function parseCSV(csvData) {
  // Excuse this monstrosity of a function, it's a necessary to parse CSV data with quotes and commas (without using a library).
  const lines = csvData.trim().split("\n");

  // Function to split CSV line by commas, respecting quotes.
  const splitCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      // Toggle inQuotes when encountering a quote character.
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        // If not inside quotes, treat comma as a field separator.
        result.push(current);
        current = "";
      } else {
        // Otherwise, add the character to the current field.
        current += char;
      }
    }

    // Add the last field.
    result.push(current);

    return result.map((field) => field.trim().replace(/^"|"$/g, "")); // Trim quotes from around the field.
  };

  // Parse headers (first line)
  const rawHeaders = splitCSVLine(lines[0]);
  csvHeaders.value = rawHeaders.map((header) => ({
    title: header,
    key: header,
  }));

  // Parse rows (from the second line onwards)
  csvRows.value = lines.slice(1).map((line) => {
    const cells = splitCSVLine(line);
    // Create an object where keys are headers and values are cells
    const rowObject = {};
    rawHeaders.forEach((header, index) => {
      rowObject[header] = cells[index] || ""; // Handle empty values
    });
    return rowObject;
  });
}

onMounted(() => {
  selectedDate.value = today; // Auto select today's date for the date picker.
});

async function resetButton() {
  // Default all reactive variables to reset the page.
  csvHeaders.value = [];
  csvRows.value = [];
  selectedDate.value = today;
  referenceNumber.value = "";
  popupPane.value = false;
  popupMessage.value = "";
}

async function removeReference() {
  showWarningDialog.value = false;
  loading.value = true;

  try {
    const result = await apiQuery("REPAIRS", "DeleteDPD", {
      type: "TOTE", // Type of request, TOTE or NONTOTE. Hardcoding to the former.
      date: selectedDate.value.split("-").reverse().join("/"), // Convert date to string in the format "dd/mm/yyyy".
      reference: referenceNumber.value, // Reference number to remove.
      operation: "delete", // Operation to perform.
    });

    await processResult(result); // Process the result from the API call.
  } catch (error) {
    loading.value = false;
    console.error("Error removing reference", error);
  }
}

async function processResult(data) {
  let result = data[0];

  switch (result.status) {
    case undefined: // No status returned, assume error.
      loading.value = false;
      popupTitle.value = "Error";
      popupMessage.value =
        "Error retrieving data. Please check the logs for more information.";
      popupPane.value = true;
      return;
    case "Success":
      loading.value = false;
      if (result.data == "") {
        // If there is a DB entry, but nothing in it yet.
        popupTitle.value = "Warning";
        popupMessage.value = "No references currently available for this date.";
        popupPane.value = true;
        return;
      } else {
        await parseCSV(result.data); // Parse the CSV data from the result into the variables.
      }
      return;
    case "Failed":
      loading.value = false;
      popupTitle.value = "Failed";
      popupMessage.value = result.message;
      popupPane.value = true;
      return;
    case "Deleted":
      loading.value = false;
      popupTitle.value = "Deleted";
      popupMessage.value = result.message;
      popupPane.value = true;
      await fetchAndParseCSV(); // Fetch and parse the CSV data again to refresh the table.
      return;
    default:
      loading.value = false;
  }
}

// Function to fetch CSV data (simulate API call)
async function fetchAndParseCSV() {
  loading.value = true;
  try {
    const result = await apiQuery("REPAIRS", "DeleteDPD", {
      type: "TOTE", // Type of request, TOTE or NONTOTE. Hardcoding to the former.
      date: selectedDate.value.split("-").reverse().join("/"), // Convert date to string in the format "dd/mm/yyyy".
      operation: "get", // Operation to perform.
    });

    await processResult(result); // Process the result from the API call.
  } catch (error) {
    loading.value = false;
    console.error("Error fetching or parsing CSV:", error);
  }
}
</script>

<template>
  <v-row>
    <v-col>
      <h2>Remove Consignment</h2>
    </v-col>
  </v-row>
  <v-sheet v-if="csvRows.length === 0">
    <v-col class="flex-column">
      <v-text-field
        v-model="selectedDate"
        label="Select TOTE date"
        variant="outlined"
        density="comfortable"
        style="max-width: 200px"
        type="date"
      ></v-text-field>

      <v-btn
        @click="fetchAndParseCSV"
        color="primary"
        :loading="loading"
        width="200"
      >
        Load Consignments
      </v-btn>
    </v-col>
  </v-sheet>

  <v-sheet v-else>
    <v-row>
      <v-col>
        <v-btn @click="resetButton" color="primary" class="mb-4"> Reset </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="new Date(selectedDate) >= new Date(today)">
      <v-col class="d-flex">
        <v-text-field
          v-model="referenceNumber"
          label="Reference Number"
          variant="outlined"
          density="comfortable"
          style="max-width: 200px"
          class="me-4"
        ></v-text-field>
        <v-btn
          @click="showWarningDialog = true"
          color="primary"
          variant="outlined"
          height="48px"
        >
          Remove Reference
        </v-btn>
      </v-col>
    </v-row>

    <v-card-text>
      <h2>
        References stored for this date -
        {{ selectedDate.split("-").reverse().join("/") }}
      </h2>
      <v-data-table
        :headers="csvHeaders"
        :items="csvRows"
        density="compact"
        class="elevation-2"
        :items-per-page="50"
        no-data-text="Run query to load data"
      ></v-data-table> </v-card-text
  ></v-sheet>
  <v-dialog v-model="showWarningDialog" max-width="500" persistent>
    <div class="text-center">
      <v-card>
        <v-card-text
          ><h3>
            Are you sure you want to remove {{ referenceNumber }}
          </h3></v-card-text
        >
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn @click="showWarningDialog = false">Cancel</v-btn>
          <v-btn
            @click="removeReference()"
            class="flex-grow-1"
            variant="tonal"
            color="primary"
            >Remove</v-btn
          >
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
  <div class="text-center">
    <v-snackbar v-model="popupPane" vertical>
      <div class="text-subtitle-1 pb-2">{{ popupTitle }}</div>
      <p>{{ popupMessage }}</p>
    </v-snackbar>
  </div>
</template>
